import _classCallCheck from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _get from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/get.js";
import _inherits from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/inherits.js";
import _possibleConstructorReturn from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn.js";
import _getPrototypeOf from "/builds/everlywell/apps/www/node_modules/@babel/runtime/helpers/esm/getPrototypeOf.js";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

/* eslint-disable no-use-before-define */
import { Base, WordArray, BufferedBlockAlgorithm } from './core.js';
import { Base64 } from './enc-base64.js';
import { EvpKDFAlgo } from './evpkdf.js';
/**
 * Abstract base cipher template.
 *
 * @property {number} keySize This cipher's key size. Default: 4 (128 bits)
 * @property {number} ivSize This cipher's IV size. Default: 4 (128 bits)
 * @property {number} _ENC_XFORM_MODE A constant representing encryption mode.
 * @property {number} _DEC_XFORM_MODE A constant representing decryption mode.
 */

export var Cipher = /*#__PURE__*/function (_BufferedBlockAlgorit) {
  _inherits(Cipher, _BufferedBlockAlgorit);

  var _super = _createSuper(Cipher);

  /**
   * Initializes a newly created cipher.
   *
   * @param {number} xformMode Either the encryption or decryption transormation mode constant.
   * @param {WordArray} key The key.
   * @param {Object} cfg (Optional) The configuration options to use for this operation.
   *
   * @example
   *
   *     const cipher = CryptoJS.algo.AES.create(
   *       CryptoJS.algo.AES._ENC_XFORM_MODE, keyWordArray, { iv: ivWordArray }
   *     );
   */
  function Cipher(xformMode, key, cfg) {
    var _this;

    _classCallCheck(this, Cipher);

    _this = _super.call(this);
    /**
     * Configuration options.
     *
     * @property {WordArray} iv The IV to use for this operation.
     */

    _this.cfg = Object.assign(new Base(), cfg); // Store transform mode and key

    _this._xformMode = xformMode;
    _this._key = key; // Set initial values

    _this.reset();

    return _this;
  }
  /**
   * Creates this cipher in encryption mode.
   *
   * @param {WordArray} key The key.
   * @param {Object} cfg (Optional) The configuration options to use for this operation.
   *
   * @return {Cipher} A cipher instance.
   *
   * @static
   *
   * @example
   *
   *     const cipher = CryptoJS.algo.AES.createEncryptor(keyWordArray, { iv: ivWordArray });
   */


  _createClass(Cipher, [{
    key: "reset",
    value:
    /**
     * Resets this cipher to its initial state.
     *
     * @example
     *
     *     cipher.reset();
     */
    function reset() {
      // Reset data buffer
      _get(_getPrototypeOf(Cipher.prototype), "reset", this).call(this); // Perform concrete-cipher logic


      this._doReset();
    }
    /**
     * Adds data to be encrypted or decrypted.
     *
     * @param {WordArray|string} dataUpdate The data to encrypt or decrypt.
     *
     * @return {WordArray} The data after processing.
     *
     * @example
     *
     *     const encrypted = cipher.process('data');
     *     const encrypted = cipher.process(wordArray);
     */

  }, {
    key: "process",
    value: function process(dataUpdate) {
      // Append
      this._append(dataUpdate); // Process available blocks


      return this._process();
    }
    /**
     * Finalizes the encryption or decryption process.
     * Note that the finalize operation is effectively a destructive, read-once operation.
     *
     * @param {WordArray|string} dataUpdate The final data to encrypt or decrypt.
     *
     * @return {WordArray} The data after final processing.
     *
     * @example
     *
     *     const encrypted = cipher.finalize();
     *     const encrypted = cipher.finalize('data');
     *     const encrypted = cipher.finalize(wordArray);
     */

  }, {
    key: "finalize",
    value: function finalize(dataUpdate) {
      // Final data update
      if (dataUpdate) {
        this._append(dataUpdate);
      } // Perform concrete-cipher logic


      var finalProcessedData = this._doFinalize();

      return finalProcessedData;
    }
  }], [{
    key: "createEncryptor",
    value: function createEncryptor(key, cfg) {
      return this.create(this._ENC_XFORM_MODE, key, cfg);
    }
    /**
     * Creates this cipher in decryption mode.
     *
     * @param {WordArray} key The key.
     * @param {Object} cfg (Optional) The configuration options to use for this operation.
     *
     * @return {Cipher} A cipher instance.
     *
     * @static
     *
     * @example
     *
     *     const cipher = CryptoJS.algo.AES.createDecryptor(keyWordArray, { iv: ivWordArray });
     */

  }, {
    key: "createDecryptor",
    value: function createDecryptor(key, cfg) {
      return this.create(this._DEC_XFORM_MODE, key, cfg);
    }
    /**
     * Creates shortcut functions to a cipher's object interface.
     *
     * @param {Cipher} cipher The cipher to create a helper for.
     *
     * @return {Object} An object with encrypt and decrypt shortcut functions.
     *
     * @static
     *
     * @example
     *
     *     const AES = CryptoJS.lib.Cipher._createHelper(CryptoJS.algo.AES);
     */

  }, {
    key: "_createHelper",
    value: function _createHelper(SubCipher) {
      var selectCipherStrategy = function selectCipherStrategy(key) {
        if (typeof key === 'string') {
          return PasswordBasedCipher;
        }

        return SerializableCipher;
      };

      return {
        encrypt: function encrypt(message, key, cfg) {
          return selectCipherStrategy(key).encrypt(SubCipher, message, key, cfg);
        },
        decrypt: function decrypt(ciphertext, key, cfg) {
          return selectCipherStrategy(key).decrypt(SubCipher, ciphertext, key, cfg);
        }
      };
    }
  }]);

  return Cipher;
}(BufferedBlockAlgorithm);
Cipher._ENC_XFORM_MODE = 1;
Cipher._DEC_XFORM_MODE = 2;
Cipher.keySize = 128 / 32;
Cipher.ivSize = 128 / 32;
/**
 * Abstract base stream cipher template.
 *
 * @property {number} blockSize
 *
 *     The number of 32-bit words this cipher operates on. Default: 1 (32 bits)
 */

export var StreamCipher = /*#__PURE__*/function (_Cipher) {
  _inherits(StreamCipher, _Cipher);

  var _super2 = _createSuper(StreamCipher);

  function StreamCipher() {
    var _this2;

    _classCallCheck(this, StreamCipher);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this2 = _super2.call.apply(_super2, [this].concat(args));
    _this2.blockSize = 1;
    return _this2;
  }

  _createClass(StreamCipher, [{
    key: "_doFinalize",
    value: function _doFinalize() {
      // Process partial blocks
      var finalProcessedBlocks = this._process(!!'flush');

      return finalProcessedBlocks;
    }
  }]);

  return StreamCipher;
}(Cipher);
/**
 * Abstract base block cipher mode template.
 */

export var BlockCipherMode = /*#__PURE__*/function (_Base) {
  _inherits(BlockCipherMode, _Base);

  var _super3 = _createSuper(BlockCipherMode);

  /**
   * Initializes a newly created mode.
   *
   * @param {Cipher} cipher A block cipher instance.
   * @param {Array} iv The IV words.
   *
   * @example
   *
   *     const mode = CryptoJS.mode.CBC.Encryptor.create(cipher, iv.words);
   */
  function BlockCipherMode(cipher, iv) {
    var _this3;

    _classCallCheck(this, BlockCipherMode);

    _this3 = _super3.call(this);
    _this3._cipher = cipher;
    _this3._iv = iv;
    return _this3;
  }
  /**
   * Creates this mode for encryption.
   *
   * @param {Cipher} cipher A block cipher instance.
   * @param {Array} iv The IV words.
   *
   * @static
   *
   * @example
   *
   *     const mode = CryptoJS.mode.CBC.createEncryptor(cipher, iv.words);
   */


  _createClass(BlockCipherMode, null, [{
    key: "createEncryptor",
    value: function createEncryptor(cipher, iv) {
      return this.Encryptor.create(cipher, iv);
    }
    /**
     * Creates this mode for decryption.
     *
     * @param {Cipher} cipher A block cipher instance.
     * @param {Array} iv The IV words.
     *
     * @static
     *
     * @example
     *
     *     const mode = CryptoJS.mode.CBC.createDecryptor(cipher, iv.words);
     */

  }, {
    key: "createDecryptor",
    value: function createDecryptor(cipher, iv) {
      return this.Decryptor.create(cipher, iv);
    }
  }]);

  return BlockCipherMode;
}(Base);

function xorBlock(words, offset, blockSize) {
  var _words = words;
  var block; // Shortcut

  var iv = this._iv; // Choose mixing block

  if (iv) {
    block = iv; // Remove IV for subsequent blocks

    this._iv = undefined;
  } else {
    block = this._prevBlock;
  } // XOR blocks


  for (var i = 0; i < blockSize; i += 1) {
    _words[offset + i] ^= block[i];
  }
}
/**
 * Cipher Block Chaining mode.
 */

/**
 * Abstract base CBC mode.
 */


export var CBC = /*#__PURE__*/function (_BlockCipherMode) {
  _inherits(CBC, _BlockCipherMode);

  var _super4 = _createSuper(CBC);

  function CBC() {
    _classCallCheck(this, CBC);

    return _super4.apply(this, arguments);
  }

  return _createClass(CBC);
}(BlockCipherMode);
/**
 * CBC encryptor.
 */

CBC.Encryptor = /*#__PURE__*/function (_CBC) {
  _inherits(_class, _CBC);

  var _super5 = _createSuper(_class);

  function _class() {
    _classCallCheck(this, _class);

    return _super5.apply(this, arguments);
  }

  _createClass(_class, [{
    key: "processBlock",
    value:
    /**
     * Processes the data block at offset.
     *
     * @param {Array} words The data words to operate on.
     * @param {number} offset The offset where the block starts.
     *
     * @example
     *
     *     mode.processBlock(data.words, offset);
     */
    function processBlock(words, offset) {
      // Shortcuts
      var cipher = this._cipher;
      var blockSize = cipher.blockSize; // XOR and encrypt

      xorBlock.call(this, words, offset, blockSize);
      cipher.encryptBlock(words, offset); // Remember this block to use with next block

      this._prevBlock = words.slice(offset, offset + blockSize);
    }
  }]);

  return _class;
}(CBC);
/**
 * CBC decryptor.
 */


CBC.Decryptor = /*#__PURE__*/function (_CBC2) {
  _inherits(_class2, _CBC2);

  var _super6 = _createSuper(_class2);

  function _class2() {
    _classCallCheck(this, _class2);

    return _super6.apply(this, arguments);
  }

  _createClass(_class2, [{
    key: "processBlock",
    value:
    /**
     * Processes the data block at offset.
     *
     * @param {Array} words The data words to operate on.
     * @param {number} offset The offset where the block starts.
     *
     * @example
     *
     *     mode.processBlock(data.words, offset);
     */
    function processBlock(words, offset) {
      // Shortcuts
      var cipher = this._cipher;
      var blockSize = cipher.blockSize; // Remember this block to use with next block

      var thisBlock = words.slice(offset, offset + blockSize); // Decrypt and XOR

      cipher.decryptBlock(words, offset);
      xorBlock.call(this, words, offset, blockSize); // This block becomes the previous block

      this._prevBlock = thisBlock;
    }
  }]);

  return _class2;
}(CBC);
/**
 * PKCS #5/7 padding strategy.
 */


export var Pkcs7 = {
  /**
   * Pads data using the algorithm defined in PKCS #5/7.
   *
   * @param {WordArray} data The data to pad.
   * @param {number} blockSize The multiple that the data should be padded to.
   *
   * @static
   *
   * @example
   *
   *     CryptoJS.pad.Pkcs7.pad(wordArray, 4);
   */
  pad: function pad(data, blockSize) {
    // Shortcut
    var blockSizeBytes = blockSize * 4; // Count padding bytes

    var nPaddingBytes = blockSizeBytes - data.sigBytes % blockSizeBytes; // Create padding word

    var paddingWord = nPaddingBytes << 24 | nPaddingBytes << 16 | nPaddingBytes << 8 | nPaddingBytes; // Create padding

    var paddingWords = [];

    for (var i = 0; i < nPaddingBytes; i += 4) {
      paddingWords.push(paddingWord);
    }

    var padding = WordArray.create(paddingWords, nPaddingBytes); // Add padding

    data.concat(padding);
  },

  /**
   * Unpads data that had been padded using the algorithm defined in PKCS #5/7.
   *
   * @param {WordArray} data The data to unpad.
   *
   * @static
   *
   * @example
   *
   *     CryptoJS.pad.Pkcs7.unpad(wordArray);
   */
  unpad: function unpad(data) {
    var _data = data; // Get number of padding bytes from last byte

    var nPaddingBytes = _data.words[_data.sigBytes - 1 >>> 2] & 0xff; // Remove padding

    _data.sigBytes -= nPaddingBytes;
  }
};
/**
 * Abstract base block cipher template.
 *
 * @property {number} blockSize
 *
 *    The number of 32-bit words this cipher operates on. Default: 4 (128 bits)
 */

export var BlockCipher = /*#__PURE__*/function (_Cipher2) {
  _inherits(BlockCipher, _Cipher2);

  var _super7 = _createSuper(BlockCipher);

  function BlockCipher(xformMode, key, cfg) {
    var _this4;

    _classCallCheck(this, BlockCipher);

    /**
     * Configuration options.
     *
     * @property {Mode} mode The block mode to use. Default: CBC
     * @property {Padding} padding The padding strategy to use. Default: Pkcs7
     */
    _this4 = _super7.call(this, xformMode, key, Object.assign({
      mode: CBC,
      padding: Pkcs7
    }, cfg));
    _this4.blockSize = 128 / 32;
    return _this4;
  }

  _createClass(BlockCipher, [{
    key: "reset",
    value: function reset() {
      var modeCreator; // Reset cipher

      _get(_getPrototypeOf(BlockCipher.prototype), "reset", this).call(this); // Shortcuts


      var cfg = this.cfg;
      var iv = cfg.iv,
          mode = cfg.mode; // Reset block mode

      if (this._xformMode === this.constructor._ENC_XFORM_MODE) {
        modeCreator = mode.createEncryptor;
      } else
        /* if (this._xformMode == this._DEC_XFORM_MODE) */
        {
          modeCreator = mode.createDecryptor; // Keep at least one block in the buffer for unpadding

          this._minBufferSize = 1;
        }

      this._mode = modeCreator.call(mode, this, iv && iv.words);
      this._mode.__creator = modeCreator;
    }
  }, {
    key: "_doProcessBlock",
    value: function _doProcessBlock(words, offset) {
      this._mode.processBlock(words, offset);
    }
  }, {
    key: "_doFinalize",
    value: function _doFinalize() {
      var finalProcessedBlocks; // Shortcut

      var padding = this.cfg.padding; // Finalize

      if (this._xformMode === this.constructor._ENC_XFORM_MODE) {
        // Pad data
        padding.pad(this._data, this.blockSize); // Process final blocks

        finalProcessedBlocks = this._process(!!'flush');
      } else
        /* if (this._xformMode == this._DEC_XFORM_MODE) */
        {
          // Process final blocks
          finalProcessedBlocks = this._process(!!'flush'); // Unpad data

          padding.unpad(finalProcessedBlocks);
        }

      return finalProcessedBlocks;
    }
  }]);

  return BlockCipher;
}(Cipher);
/**
 * A collection of cipher parameters.
 *
 * @property {WordArray} ciphertext The raw ciphertext.
 * @property {WordArray} key The key to this ciphertext.
 * @property {WordArray} iv The IV used in the ciphering operation.
 * @property {WordArray} salt The salt used with a key derivation function.
 * @property {Cipher} algorithm The cipher algorithm.
 * @property {Mode} mode The block mode used in the ciphering operation.
 * @property {Padding} padding The padding scheme used in the ciphering operation.
 * @property {number} blockSize The block size of the cipher.
 * @property {Format} formatter
 *    The default formatting strategy to convert this cipher params object to a string.
 */

export var CipherParams = /*#__PURE__*/function (_Base2) {
  _inherits(CipherParams, _Base2);

  var _super8 = _createSuper(CipherParams);

  /**
   * Initializes a newly created cipher params object.
   *
   * @param {Object} cipherParams An object with any of the possible cipher parameters.
   *
   * @example
   *
   *     var cipherParams = CryptoJS.lib.CipherParams.create({
   *         ciphertext: ciphertextWordArray,
   *         key: keyWordArray,
   *         iv: ivWordArray,
   *         salt: saltWordArray,
   *         algorithm: CryptoJS.algo.AES,
   *         mode: CryptoJS.mode.CBC,
   *         padding: CryptoJS.pad.PKCS7,
   *         blockSize: 4,
   *         formatter: CryptoJS.format.OpenSSL
   *     });
   */
  function CipherParams(cipherParams) {
    var _this5;

    _classCallCheck(this, CipherParams);

    _this5 = _super8.call(this);

    _this5.mixIn(cipherParams);

    return _this5;
  }
  /**
   * Converts this cipher params object to a string.
   *
   * @param {Format} formatter (Optional) The formatting strategy to use.
   *
   * @return {string} The stringified cipher params.
   *
   * @throws Error If neither the formatter nor the default formatter is set.
   *
   * @example
   *
   *     var string = cipherParams + '';
   *     var string = cipherParams.toString();
   *     var string = cipherParams.toString(CryptoJS.format.OpenSSL);
   */


  _createClass(CipherParams, [{
    key: "toString",
    value: function toString(formatter) {
      return (formatter || this.formatter).stringify(this);
    }
  }]);

  return CipherParams;
}(Base);
/**
 * OpenSSL formatting strategy.
 */

export var OpenSSLFormatter = {
  /**
   * Converts a cipher params object to an OpenSSL-compatible string.
   *
   * @param {CipherParams} cipherParams The cipher params object.
   *
   * @return {string} The OpenSSL-compatible string.
   *
   * @static
   *
   * @example
   *
   *     var openSSLString = CryptoJS.format.OpenSSL.stringify(cipherParams);
   */
  stringify: function stringify(cipherParams) {
    var wordArray; // Shortcuts

    var ciphertext = cipherParams.ciphertext,
        salt = cipherParams.salt; // Format

    if (salt) {
      wordArray = WordArray.create([0x53616c74, 0x65645f5f]).concat(salt).concat(ciphertext);
    } else {
      wordArray = ciphertext;
    }

    return wordArray.toString(Base64);
  },

  /**
   * Converts an OpenSSL-compatible string to a cipher params object.
   *
   * @param {string} openSSLStr The OpenSSL-compatible string.
   *
   * @return {CipherParams} The cipher params object.
   *
   * @static
   *
   * @example
   *
   *     var cipherParams = CryptoJS.format.OpenSSL.parse(openSSLString);
   */
  parse: function parse(openSSLStr) {
    var salt; // Parse base64

    var ciphertext = Base64.parse(openSSLStr); // Shortcut

    var ciphertextWords = ciphertext.words; // Test for salt

    if (ciphertextWords[0] === 0x53616c74 && ciphertextWords[1] === 0x65645f5f) {
      // Extract salt
      salt = WordArray.create(ciphertextWords.slice(2, 4)); // Remove salt from ciphertext

      ciphertextWords.splice(0, 4);
      ciphertext.sigBytes -= 16;
    }

    return CipherParams.create({
      ciphertext: ciphertext,
      salt: salt
    });
  }
};
/**
 * A cipher wrapper that returns ciphertext as a serializable cipher params object.
 */

export var SerializableCipher = /*#__PURE__*/function (_Base3) {
  _inherits(SerializableCipher, _Base3);

  var _super9 = _createSuper(SerializableCipher);

  function SerializableCipher() {
    _classCallCheck(this, SerializableCipher);

    return _super9.apply(this, arguments);
  }

  _createClass(SerializableCipher, null, [{
    key: "encrypt",
    value:
    /**
     * Encrypts a message.
     *
     * @param {Cipher} cipher The cipher algorithm to use.
     * @param {WordArray|string} message The message to encrypt.
     * @param {WordArray} key The key.
     * @param {Object} cfg (Optional) The configuration options to use for this operation.
     *
     * @return {CipherParams} A cipher params object.
     *
     * @static
     *
     * @example
     *
     *     var ciphertextParams = CryptoJS.lib.SerializableCipher
     *       .encrypt(CryptoJS.algo.AES, message, key);
     *     var ciphertextParams = CryptoJS.lib.SerializableCipher
     *       .encrypt(CryptoJS.algo.AES, message, key, { iv: iv });
     *     var ciphertextParams = CryptoJS.lib.SerializableCipher
     *       .encrypt(CryptoJS.algo.AES, message, key, { iv: iv, format: CryptoJS.format.OpenSSL });
     */
    function encrypt(cipher, message, key, cfg) {
      // Apply config defaults
      var _cfg = Object.assign(new Base(), this.cfg, cfg); // Encrypt


      var encryptor = cipher.createEncryptor(key, _cfg);
      var ciphertext = encryptor.finalize(message); // Shortcut

      var cipherCfg = encryptor.cfg; // Create and return serializable cipher params

      return CipherParams.create({
        ciphertext: ciphertext,
        key: key,
        iv: cipherCfg.iv,
        algorithm: cipher,
        mode: cipherCfg.mode,
        padding: cipherCfg.padding,
        blockSize: encryptor.blockSize,
        formatter: _cfg.format
      });
    }
    /**
     * Decrypts serialized ciphertext.
     *
     * @param {Cipher} cipher The cipher algorithm to use.
     * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
     * @param {WordArray} key The key.
     * @param {Object} cfg (Optional) The configuration options to use for this operation.
     *
     * @return {WordArray} The plaintext.
     *
     * @static
     *
     * @example
     *
     *     var plaintext = CryptoJS.lib.SerializableCipher
     *       .decrypt(CryptoJS.algo.AES, formattedCiphertext, key,
     *         { iv: iv, format: CryptoJS.format.OpenSSL });
     *     var plaintext = CryptoJS.lib.SerializableCipher
     *       .decrypt(CryptoJS.algo.AES, ciphertextParams, key,
     *         { iv: iv, format: CryptoJS.format.OpenSSL });
     */

  }, {
    key: "decrypt",
    value: function decrypt(cipher, ciphertext, key, cfg) {
      var _ciphertext = ciphertext; // Apply config defaults

      var _cfg = Object.assign(new Base(), this.cfg, cfg); // Convert string to CipherParams


      _ciphertext = this._parse(_ciphertext, _cfg.format); // Decrypt

      var plaintext = cipher.createDecryptor(key, _cfg).finalize(_ciphertext.ciphertext);
      return plaintext;
    }
    /**
     * Converts serialized ciphertext to CipherParams,
     * else assumed CipherParams already and returns ciphertext unchanged.
     *
     * @param {CipherParams|string} ciphertext The ciphertext.
     * @param {Formatter} format The formatting strategy to use to parse serialized ciphertext.
     *
     * @return {CipherParams} The unserialized ciphertext.
     *
     * @static
     *
     * @example
     *
     *     var ciphertextParams = CryptoJS.lib.SerializableCipher
     *       ._parse(ciphertextStringOrParams, format);
     */

  }, {
    key: "_parse",
    value: function _parse(ciphertext, format) {
      if (typeof ciphertext === 'string') {
        return format.parse(ciphertext, this);
      }

      return ciphertext;
    }
  }]);

  return SerializableCipher;
}(Base);
/**
 * Configuration options.
 *
 * @property {Formatter} format
 *
 *    The formatting strategy to convert cipher param objects to and from a string.
 *    Default: OpenSSL
 */

SerializableCipher.cfg = Object.assign(new Base(), {
  format: OpenSSLFormatter
});
/**
 * OpenSSL key derivation function.
 */

export var OpenSSLKdf = {
  /**
   * Derives a key and IV from a password.
   *
   * @param {string} password The password to derive from.
   * @param {number} keySize The size in words of the key to generate.
   * @param {number} ivSize The size in words of the IV to generate.
   * @param {WordArray|string} salt
   *     (Optional) A 64-bit salt to use. If omitted, a salt will be generated randomly.
   *
   * @return {CipherParams} A cipher params object with the key, IV, and salt.
   *
   * @static
   *
   * @example
   *
   *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32);
   *     var derivedParams = CryptoJS.kdf.OpenSSL.execute('Password', 256/32, 128/32, 'saltsalt');
   */
  execute: function execute(password, keySize, ivSize, salt) {
    var _salt = salt; // Generate random salt

    if (!_salt) {
      _salt = WordArray.random(64 / 8);
    } // Derive key and IV


    var key = EvpKDFAlgo.create({
      keySize: keySize + ivSize
    }).compute(password, _salt); // Separate key and IV

    var iv = WordArray.create(key.words.slice(keySize), ivSize * 4);
    key.sigBytes = keySize * 4; // Return params

    return CipherParams.create({
      key: key,
      iv: iv,
      salt: _salt
    });
  }
};
/**
 * A serializable cipher wrapper that derives the key from a password,
 * and returns ciphertext as a serializable cipher params object.
 */

export var PasswordBasedCipher = /*#__PURE__*/function (_SerializableCipher) {
  _inherits(PasswordBasedCipher, _SerializableCipher);

  var _super10 = _createSuper(PasswordBasedCipher);

  function PasswordBasedCipher() {
    _classCallCheck(this, PasswordBasedCipher);

    return _super10.apply(this, arguments);
  }

  _createClass(PasswordBasedCipher, null, [{
    key: "encrypt",
    value:
    /**
     * Encrypts a message using a password.
     *
     * @param {Cipher} cipher The cipher algorithm to use.
     * @param {WordArray|string} message The message to encrypt.
     * @param {string} password The password.
     * @param {Object} cfg (Optional) The configuration options to use for this operation.
     *
     * @return {CipherParams} A cipher params object.
     *
     * @static
     *
     * @example
     *
     *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher
     *       .encrypt(CryptoJS.algo.AES, message, 'password');
     *     var ciphertextParams = CryptoJS.lib.PasswordBasedCipher
     *       .encrypt(CryptoJS.algo.AES, message, 'password', { format: CryptoJS.format.OpenSSL });
     */
    function encrypt(cipher, message, password, cfg) {
      // Apply config defaults
      var _cfg = Object.assign(new Base(), this.cfg, cfg); // Derive key and other params


      var derivedParams = _cfg.kdf.execute(password, cipher.keySize, cipher.ivSize); // Add IV to config


      _cfg.iv = derivedParams.iv; // Encrypt

      var ciphertext = SerializableCipher.encrypt.call(this, cipher, message, derivedParams.key, _cfg); // Mix in derived params

      ciphertext.mixIn(derivedParams);
      return ciphertext;
    }
    /**
     * Decrypts serialized ciphertext using a password.
     *
     * @param {Cipher} cipher The cipher algorithm to use.
     * @param {CipherParams|string} ciphertext The ciphertext to decrypt.
     * @param {string} password The password.
     * @param {Object} cfg (Optional) The configuration options to use for this operation.
     *
     * @return {WordArray} The plaintext.
     *
     * @static
     *
     * @example
     *
     *     var plaintext = CryptoJS.lib.PasswordBasedCipher
     *       .decrypt(CryptoJS.algo.AES, formattedCiphertext, 'password',
     *         { format: CryptoJS.format.OpenSSL });
     *     var plaintext = CryptoJS.lib.PasswordBasedCipher
     *       .decrypt(CryptoJS.algo.AES, ciphertextParams, 'password',
     *         { format: CryptoJS.format.OpenSSL });
     */

  }, {
    key: "decrypt",
    value: function decrypt(cipher, ciphertext, password, cfg) {
      var _ciphertext = ciphertext; // Apply config defaults

      var _cfg = Object.assign(new Base(), this.cfg, cfg); // Convert string to CipherParams


      _ciphertext = this._parse(_ciphertext, _cfg.format); // Derive key and other params

      var derivedParams = _cfg.kdf.execute(password, cipher.keySize, cipher.ivSize, _ciphertext.salt); // Add IV to config


      _cfg.iv = derivedParams.iv; // Decrypt

      var plaintext = SerializableCipher.decrypt.call(this, cipher, _ciphertext, derivedParams.key, _cfg);
      return plaintext;
    }
  }]);

  return PasswordBasedCipher;
}(SerializableCipher);
/**
 * Configuration options.
 *
 * @property {KDF} kdf
 *     The key derivation function to use to generate a key and IV from a password.
 *     Default: OpenSSL
 */

PasswordBasedCipher.cfg = Object.assign(SerializableCipher.cfg, {
  kdf: OpenSSLKdf
});